/**
 * auth.ts — portal session handling.
 *
 * Pilot model (Doc §5.1): a tenant member signs in and the portal holds a tenant-scoped bearer
 * token used for every API call. Today that token is the tenant API key (or an SSO/custom token)
 * the member pastes at login; the planned upgrade is email/password → minted session token and
 * enterprise OIDC. The token never leaves the browser except as the `Authorization` header.
 *
 * The chosen portal ROLE gates the UI (which actions are shown). The backend `requireScope` on the
 * token's own scopes remains the real enforcement boundary, so a UI that shows too much still cannot
 * perform an action the token is not scoped for — the call returns 403 and we surface it.
 */

import { normalizeCvsiError, CvsiError } from '@cvsi/shared';
import type { CustomerPortalRole, KeyMode } from '@cvsi/shared';

const KEY = 'cvsi.portal.session';

export interface PortalSession {
  baseUrl: string;
  token: string;
  role: CustomerPortalRole;
  environment: KeyMode;
  tenantId?: string;
  email?: string;
}

export interface PortalLoginResult {
  token: string;
  tenantId: string;
  email: string;
  role: CustomerPortalRole;
  scopes: string[];
  expiresInSeconds: number;
}

export function loadSession(): PortalSession | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as PortalSession) : null;
  } catch {
    return null;
  }
}

export function saveSession(s: PortalSession): void {
  sessionStorage.setItem(KEY, JSON.stringify(s));
}

export function clearSession(): void {
  sessionStorage.removeItem(KEY);
}

/** Default API base URL from build-time env, falling back to the deployed CVSI API. */
export function defaultBaseUrl(): string {
  return (import.meta.env.VITE_CVSI_BASE_URL as string) || 'https://cvsi-3qqpinimfq-uc.a.run.app/v1';
}

export async function loginWithPassword(baseUrl: string, body: { tenantId: string; email: string; password: string }): Promise<PortalLoginResult> {
  let res: Response;
  try {
    res = await fetch(`${baseUrl.replace(/\/$/, '')}/portal/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new CvsiError('network', 0, err instanceof Error ? err.message : 'Network request failed');
  }
  if (!res.ok) throw await normalizeCvsiError(res);
  return (await res.json()) as PortalLoginResult;
}

/** Google Client ID used for the "Sign in with Google" button, or null if the feature isn't configured. */
export function googleClientId(): string | null {
  return (import.meta.env.VITE_GOOGLE_CLIENT_ID as string) || null;
}

export async function loadAuthConfig(baseUrl: string): Promise<{ googleClientId: string | null }> {
  let res: Response;
  try {
    res = await fetch(`${baseUrl.replace(/\/$/, '')}/portal/config`);
  } catch {
    return { googleClientId: googleClientId() };
  }
  if (!res.ok) return { googleClientId: googleClientId() };
  const body = (await res.json()) as { googleClientId?: unknown };
  return { googleClientId: typeof body.googleClientId === 'string' && body.googleClientId ? body.googleClientId : googleClientId() };
}

/** Exchange a Google ID token (from the Google Identity Services button) for a portal session. */
export async function loginWithGoogle(baseUrl: string, idToken: string): Promise<PortalLoginResult> {
  let res: Response;
  try {
    res = await fetch(`${baseUrl.replace(/\/$/, '')}/portal/login/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken }),
    });
  } catch (err) {
    throw new CvsiError('network', 0, err instanceof Error ? err.message : 'Network request failed');
  }
  if (!res.ok) throw await normalizeCvsiError(res);
  return (await res.json()) as PortalLoginResult;
}
